// Shared button that generates and downloads a compliance report PDF. Shows a spinner
// while generating and a clear error message on failure (never a silent failure). Used
// on the compliance-reports hub and on the subcontractor / engineer / asset detail
// pages. The actual download (POST then blob) lives in api.complianceReports.download.
import { useState } from "react";
import { api } from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { buttonClasses } from "../../components/Button";

export function ReportDownloadButton({
  reportType,
  parameters,
  label,
  className,
  dryRun = false,
}: {
  reportType: string;
  parameters?: Record<string, unknown>;
  label: string;
  className?: string;
  // Session AOY: render and download WITHOUT writing an entry to the generation audit trail.
  // Off by default, so every existing call site (the per-entity buttons on the subcontractor,
  // engineer and lift detail pages, and the auditor portal's own copy of the hub) is a normal,
  // logged generation and is unchanged. Only the compliance hub's test-render mode sets it.
  dryRun?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      // The third argument is passed ONLY for a test render. That keeps an ordinary
      // generation's call exactly two arguments, as it has always been, so nothing about the
      // normal path changes shape -- and { dryRun: false } would in any case be identical to
      // omitting it, since the server defaults it off.
      await (dryRun
        ? api.complianceReports.download(reportType, parameters, { dryRun: true })
        : api.complianceReports.download(reportType, parameters));
    } catch (e) {
      // X-2, Session AQV review fix: this used to prefer the raw ApiError.code over any message
      // whenever one existed, which is almost always -- so this shared button (used on the
      // compliance-reports hub and the subcontractor/engineer/asset detail pages) was showing an
      // office user, or an external auditor on the same hub, machine strings like "not_found"
      // instead of a sentence. friendlyApiError only surfaces server text that reads like one.
      setError(friendlyApiError(e, "Could not generate the report. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={run}
        disabled={busy}
        aria-busy={busy}
        className={
          // In test-render mode the button takes the amber advisory treatment rather than the
          // brand green CTA, and says so in its own label. A toggle somewhere else on the page
          // is not enough on its own: the control you actually press has to tell you which
          // thing it is about to do, or an office user leaves test mode on and quietly fails to
          // record a genuine export. Amber is the platform's locked advisory colour, and
          // text-amber-400 is the themed INK variant (Session AFK), so it stays AA-legible on
          // the light auditor/office surfaces as well as on dark.
          dryRun
            ? "rounded-ele border border-amber-500 bg-transparent px-4 py-2 font-heading text-sm tracking-wide text-amber-400 disabled:opacity-60"
            : className ?? buttonClasses("primary", "md")
        }
      >
        {busy ? "Generating..." : dryRun ? `${label} (test render, not logged)` : label}
      </button>
      {error && (
        <span role="alert" className="text-xs text-ele-danger">
          {error}
        </span>
      )}
    </span>
  );
}
